import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';

const Months = () => {
  const [months, setMonths] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchMonths();
  }, [currentPage, searchQuery]);

  const fetchMonths = async () => {
    setLoading(true);
    try {
      const response = await apiService.post('months', {
        search: searchQuery,
        start: (currentPage - 1) * 10,
        length: 10
      });
      
      if (response.data) {
        setMonths(response.data);
        setTotalPages(Math.ceil(response.recordsFiltered / 10));
      }
    } catch (error) {
      console.error('Error fetching months:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key="first" onClick={() => setCurrentPage(1)} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50">«</button>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 border rounded-lg ${currentPage === i ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-50'}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      pages.push(
        <button key="last" onClick={() => setCurrentPage(totalPages)} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50">»</button>
      );
    }

    return pages;
  };

  return (
    <div className="p-0">
      <div className="flex items-center justify-between mb-6">
        <nav className="text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/location" className="text-gray-500 hover:text-gray-700">Locations</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Months</span>
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4">
          <div className="relative w-64">
            <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Search months..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Image</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Number</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#009ef7]"></div>
                      <p className="mt-3 text-sm text-gray-500">Loading...</p>
                    </div>
                  </td>
                </tr>
              ) : months.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No months found</td>
                </tr>
              ) : (
                months.map((month, index) => (
                  <tr key={month._id} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-900">{(currentPage - 1) * 10 + index + 1}</td>
                    <td className="px-4 py-3">
                      {month.image ? (
                        <img src={`${process.env.REACT_APP_MEDIA_URL}${month.image}`} alt={month.name} className="w-12 h-12 rounded object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                          <i className="bi bi-image text-gray-400"></i>
                        </div>
                      )}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-900">{month.name}</td>
                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-900">{month.number}</td>
                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-900">{month.title || '-'}</td>
                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-500">
                      {month.sortDescription ? (month.sortDescription.length > 50 ? month.sortDescription.substring(0, 50) + '...' : month.sortDescription) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/edit-month/${month._id}`} className="px-3 py-1 text-sm bg-green-50 text-green-600 rounded hover:bg-green-100">
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && months.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, months.length)} entries
            </div>
            <div className="flex gap-1">
              {renderPagination()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Months;
